import { useCallback, useEffect, useState } from "react";
import { Send } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Card } from "./_shared";

export const SUPPORT_CAT_LABEL = {
  general:  "General",
  billing:  "Billing",
  account:  "Account",
  bug:      "Bug",
  feature:  "Feature",
  other:    "Other",
};
export const SUPPORT_STATUS_COLOR = {
  open:     "#F59E0B",
  resolved: "#10B981",
  closed:   "#71717A",
};

export function SupportTab() {
  const { apiFetch } = useAuth();
  const [params, setParams] = useSearchParams();
  const focusedId = params.get("id") || null;
  const [filter, setFilter] = useState("awaiting");  // awaiting | open | resolved | all
  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({ open: 0, awaiting_admin: 0, total: 0 });
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setList(null);
    try {
      const qs = filter === "awaiting" ? "awaiting=admin"
              : filter === "all"       ? ""
              :                          `status=${filter}`;
      const d = await apiFetch(`/api/admin/support/tickets${qs ? `?${qs}` : ""}`);
      setList(d.items || []);
      setCounts({ open: d.open || 0, awaiting_admin: d.awaiting_admin || 0, total: d.total || 0 });
    } catch { setList([]); }
  }, [apiFetch, filter]);

  const loadOne = useCallback(async (id) => {
    if (!id) { setActive(null); return; }
    try {
      const d = await apiFetch(`/api/support/tickets/${id}`);
      setActive(d.ticket);
    } catch { setActive(null); }
  }, [apiFetch]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadOne(focusedId); }, [focusedId, loadOne]);

  const openOne = (id) => setParams({ tab: "support", id });
  const back = () => { setParams({ tab: "support" }); setActive(null); loadList(); };

  const reply = async (body) => {
    if (!active) return;
    setBusy(true);
    try {
      await apiFetch(`/api/support/tickets/${active.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      await loadOne(active.id);
    } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    if (!active) return;
    setBusy(true);
    try {
      await apiFetch(`/api/support/tickets/${active.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      await loadOne(active.id);
      await loadList();
    } finally { setBusy(false); }
  };

  if (active) {
    return <AdminSupportThread ticket={active} busy={busy} onBack={back} onReply={reply} onSetStatus={setStatus} />;
  }

  return (
    <div className="space-y-4">
      <Card title="SUPPORT TICKETS"
            subtitle={`${counts.awaiting_admin} awaiting reply · ${counts.open} open · ${counts.total} total. Threaded conversations with users.`}
            accent="#8B5CF6" testid="support-card">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="support-filter">
          {[
            { k: "awaiting", label: `Awaiting (${counts.awaiting_admin})` },
            { k: "open",     label: "Open" },
            { k: "resolved", label: "Resolved" },
            { k: "all",      label: "All" },
          ].map((o) => (
            <button key={o.k} onClick={() => setFilter(o.k)}
              data-testid={`support-filter-${o.k}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border transition-colors ${filter === o.k ? "text-white bg-[#8B5CF6] border-[#8B5CF6]" : "text-[var(--srs-ink-dim)] border-[var(--srs-line)] hover:text-[var(--srs-ink)]"}`}>
              {o.label}
            </button>
          ))}
          <button onClick={loadList} className="ml-auto px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors" data-testid="support-refresh">
            Refresh
          </button>
        </div>

        {list === null && <div className="text-xs text-[var(--srs-ink-faint)]">Loading…</div>}
        {list?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="support-empty">
            No tickets {filter === "awaiting" ? "awaiting reply" : "found"}.
          </div>
        )}

        <div className="flex flex-col gap-2" data-testid="support-list">
          {list?.map((t) => {
            const awaiting = t.last_message_by === "user" && t.status !== "closed";
            return (
              <button key={t.id} onClick={() => openOne(t.id)}
                data-testid={`support-row-${t.id}`}
                className="text-left border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 hover:border-[#8B5CF6] transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="font-mono text-sm text-[var(--srs-ink)] truncate max-w-[260px]">{t.user_email}</span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                          style={{
                            background: `${SUPPORT_STATUS_COLOR[t.status]}1F`,
                            color: SUPPORT_STATUS_COLOR[t.status],
                            border: `1px solid ${SUPPORT_STATUS_COLOR[t.status]}59`,
                          }}>{t.status}</span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-surface-2)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">
                      {SUPPORT_CAT_LABEL[t.category] || t.category}
                    </span>
                    {awaiting && (
                      <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase text-white"
                            style={{ background: "#8B5CF6" }}>
                        AWAITING YOU
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-[var(--srs-ink-faint)] tracking-wide whitespace-nowrap">
                    {new Date(t.last_message_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-[var(--srs-ink)] text-sm font-semibold mb-1 truncate">{t.subject}</div>
                <div className="text-[11px] text-[var(--srs-ink-dim)]">{t.message_count} messages</div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export function AdminSupportThread({ ticket, busy, onBack, onReply, onSetStatus }) {
  const [draft, setDraft] = useState("");
  const [localErr, setLocalErr] = useState("");

  const send = async () => {
    if (!draft.trim() || busy) return;
    setLocalErr("");
    try { await onReply(draft.trim()); setDraft(""); }
    catch (e) { setLocalErr(e?.message || "Send failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-support-thread">
      <button onClick={onBack} className="text-xs tracking-[0.25em] uppercase text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors" data-testid="admin-thread-back">
        ← Back to tickets
      </button>
      <Card title={`TICKET · ${ticket.subject}`}
            subtitle={`From ${ticket.user_email} · ${SUPPORT_CAT_LABEL[ticket.category] || ticket.category} · opened ${new Date(ticket.created_at).toLocaleString()}`}
            accent="#8B5CF6" testid="admin-ticket-card">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                style={{
                  background: `${SUPPORT_STATUS_COLOR[ticket.status]}1F`,
                  color: SUPPORT_STATUS_COLOR[ticket.status],
                  border: `1px solid ${SUPPORT_STATUS_COLOR[ticket.status]}59`,
                }} data-testid="admin-ticket-status">{ticket.status}</span>
          {["open", "resolved", "closed"].filter((s) => s !== ticket.status).map((s) => (
            <button key={s} onClick={() => onSetStatus(s)}
              disabled={busy}
              data-testid={`admin-status-${s}`}
              className="px-2 py-1 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[#8B5CF6] transition-colors disabled:opacity-50">
              Mark {s}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 mb-6">
          {(ticket.messages || []).map((m) => (
            <div key={m.id} data-testid={`admin-msg-${m.id}`}
                 className={`border p-4 rounded-lg ${m.author_role === "admin" ? "border-[#8B5CF6]/40 bg-[#8B5CF6]/8" : "border-[var(--srs-line)] bg-[var(--srs-surface-2)]"}`}>
              <div className="flex items-center gap-2 mb-2 text-[11px] text-[var(--srs-ink-dim)]">
                <span className="font-bold uppercase tracking-[0.15em]" style={{ color: m.author_role === "admin" ? "#8B5CF6" : "var(--srs-ink)" }}>
                  {m.author_role === "admin" ? "Admin" : ticket.user_email}
                </span>
                <span>·</span>
                <span>{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-[var(--srs-ink)] whitespace-pre-wrap leading-relaxed">{m.body}</div>
            </div>
          ))}
        </div>

        {ticket.status === "closed" ? (
          <div className="text-xs text-[var(--srs-ink-faint)] text-center py-4 border border-dashed border-[var(--srs-line)] rounded-lg">
            Ticket is closed. Mark "open" to reopen.
          </div>
        ) : (
          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] p-3 rounded-lg">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              placeholder="Reply to the user…"
              data-testid="admin-reply-textarea"
              className="w-full bg-transparent border-0 outline-none resize-y text-sm leading-relaxed text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)]"
            />
            {localErr && <div className="text-xs text-[#EF4444] mt-1">{localErr}</div>}
            <div className="flex justify-end mt-2">
              <button onClick={send} disabled={!draft.trim() || busy}
                      data-testid="admin-reply-send"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B5CF6] text-white text-xs font-bold tracking-[0.18em] uppercase rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
                <Send className="w-3 h-3" />
                {busy ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Economics Tab — live unit-economics dashboard (owner-only).
// Pulls /api/admin/economics + lets owner watch margins as the base grows.
